import { useEffect, useMemo, useState } from 'react';
import { Alert, Input, Segmented, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SafetyOutlined } from '@ant-design/icons';
import {
  resolvePermissionMatrix,
  RESOURCE_BLURBS,
  type MatrixKey,
  type MatrixSource,
  type PermissionMatrixModel,
} from './permissionMatrix';
import { ROLE_CATALOGUE, ROLE_LABEL } from './roleCatalogue';
import { fetchServerMatrix } from '@/api/uam';
import { listRoles, type CustomRole } from '@/api/teamAccess';
import { Term } from '@/help/Term';

const { Title, Paragraph, Text } = Typography;

/**
 * Literal marks (the FTM Team&Access table pattern): a granted cell carries a visible glyph AND an
 * accessible name; an ungranted cell a muted dash. Never colour-only, never an empty cell that a
 * screen reader reads as nothing.
 */
function MarkCell({ granted, role, permKey, stepUp }: { granted: boolean; role: string; permKey: string; stepUp: boolean }) {
  const label = granted
    ? `${role} holds ${permKey}${stepUp ? ' (step-up challenge on use)' : ''}`
    : `${role} does not hold ${permKey}`;
  return (
    <span
      role="img"
      aria-label={label}
      style={{
        display: 'inline-block',
        width: '100%',
        textAlign: 'center',
        fontSize: 12.5,
        fontWeight: granted ? 700 : 400,
        color: granted ? 'var(--ol-primary)' : 'var(--ol-text-tertiary)',
      }}
    >
      {granted ? '●' : '—'}
    </span>
  );
}

/**
 * Permission-matrix authority view (OPSFL-IAMUI-1, FTM Team&Access model). READ-ONLY on purpose:
 * the matrix is the single authorization authority, versioned in the repo and loaded fail-closed by
 * the server at boot — it is not runtime data, so this table renders the very same file the running
 * server enforces (same build, same commit). There is deliberately no editor: changing the matrix
 * is a code change with build gates, never a console action.
 */
export function PermissionMatrixPanel() {
  const [matrix, setMatrix] = useState<PermissionMatrixModel | null>(null);
  const [source, setSource] = useState<MatrixSource>('bundle');
  const [parseError, setParseError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [lane, setLane] = useState<string>('All');
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);

  // SERVER-FIRST (OPSFL-THIAM-1): GET /v1/uam/matrix is what THIS server enforces; the
  // same-commit bundled copy remains the fallback (older server 404 / no-uam-read 403 /
  // unreachable). A bundle PARSE failure still renders the loud refuse-to-guess error.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const resolved = await resolvePermissionMatrix(fetchServerMatrix);
        if (alive) {
          setMatrix(resolved.model);
          setSource(resolved.source);
        }
      } catch (e) {
        if (alive) setParseError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Custom x_ roles bind these keys — show them as context. Best-effort: roles.read may 403/404
  // for this caller; the fixed matrix is still the full authority view without them.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const roles = await listRoles('ACTIVE');
        if (alive) setCustomRoles(roles);
      } catch {
        /* degrade silently — the panel notes custom roles generically */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const resources = useMemo(() => {
    if (!matrix) return [];
    return [...new Set(matrix.keys.map((k) => k.resource))];
  }, [matrix]);

  const rows = useMemo(() => {
    if (!matrix) return [];
    const q = filter.trim().toLowerCase();
    return matrix.keys.filter((k) => {
      if (lane !== 'All' && k.resource !== lane) return false;
      if (q && !k.key.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [matrix, filter, lane]);

  const columns = useMemo<ColumnsType<MatrixKey>>(() => {
    if (!matrix) return [];
    return [
      {
        title: 'Permission key',
        dataIndex: 'key',
        key: 'key',
        fixed: 'left',
        width: 260,
        render: (v: string, r) => (
          <Tooltip
            title={
              <span>
                <span className="ol-mono">{v}</span>
                <br />
                {RESOURCE_BLURBS[r.resource] ?? r.resource}
                {r.stepUp && (
                  <>
                    <br />
                    Step-up lane: using this key re-challenges the user&apos;s identity.
                  </>
                )}
              </span>
            }
          >
            {/* tabIndex so the description is reachable by keyboard, not hover-only */}
            <span tabIndex={0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, outlineOffset: 2 }}>
              <span className="ol-mono" style={{ fontSize: 12 }}>
                {v}
              </span>
              {r.stepUp && (
                <Tag color="orange" style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>
                  step-up
                </Tag>
              )}
            </span>
          </Tooltip>
        ),
      },
      ...matrix.roles.map((role) => ({
        title: (
          <Tooltip title={ROLE_CATALOGUE.find((r) => r.role === role)?.blurb ?? role}>
            <span tabIndex={0} style={{ fontSize: 11, whiteSpace: 'nowrap' as const }}>
              {ROLE_LABEL.get(role) ?? role}
            </span>
          </Tooltip>
        ),
        key: role,
        width: 92,
        align: 'center' as const,
        render: (_: unknown, r: MatrixKey) => (
          <MarkCell granted={r.roles.includes(role)} role={ROLE_LABEL.get(role) ?? role} permKey={r.key} stepUp={r.stepUp} />
        ),
      })),
    ];
  }, [matrix]);

  if (parseError) {
    return (
      <Alert
        type="error"
        showIcon
        message="The permission matrix could not be read"
        description={`The bundled matrix failed to parse — this build is inconsistent and the table refuses to guess. ${parseError}`}
      />
    );
  }

  if (!matrix) return null;

  return (
    <div>
      <div style={{ maxWidth: 860, marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          <SafetyOutlined style={{ marginRight: 8, color: 'var(--ol-primary)' }} />
          Permission matrix — who holds what
        </Title>
        <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>
          The full key × role <Term id="permission-matrix">permission matrix</Term>
          {source === 'server' ? (
            <>
              , <Text strong>read live from the running server</Text> (the exact matrix it loaded at
              boot and enforces on every call)
            </>
          ) : (
            <>
              , read from this console build&apos;s bundled copy of the versioned matrix file (the
              server&apos;s live read was not available — an older server, or your role holds no UAM
              read)
            </>
          )}{' '}
          (schema v{matrix.schemaVersion}, {matrix.keys.length} keys). It is the single authorization
          authority and is read-only here: changes are code changes with build gates, never console
          edits. Grants are explicit — no hierarchy, no inheritance. Keys marked{' '}
          <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>step-up</Tag> re-challenge the
          user&apos;s identity when used (
          <Term id="step-up-authentication">step-up authentication</Term>).
        </Paragraph>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12, maxWidth: 860 }}
        message="Guardrails the server enforces on top of this table"
        description={
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
            <li>
              <Text strong>Last-admin protection</Text> — demoting or disabling the last enabled hub
              admin is refused (<Text code>LAST_HUB_ADMIN_PROTECTED</Text>): a hub can never brick
              itself out of administration.
            </li>
            <li>
              <Text strong>Auditor exclusivity</Text> — a person holding <Text code>auditor</Text>{' '}
              may hold no other role that carries a mutating key (
              <Text code>UAM_AUDITOR_EXCLUSIVE</Text>): examiner independence is enforced about the
              person, not just the role.
            </li>
            <li>
              <Text strong>No self-administration</Text> — nobody can change their own roles, spoke,
              or status (<Text code>UAM_SELF_FORBIDDEN</Text>).
            </li>
            <li>
              <Text strong>Narrowing only</Text> — <Term id="custom-role">custom roles</Term>
              {customRoles.length
                ? ` (${customRoles.length} active in this hub: ${customRoles.map((r) => r.roleKey).join(', ')})`
                : ''}{' '}
              bind keys from this table and never add new ones; ACL{' '}
              <Term id="confinement">confinement</Term> and ABAC policies only subtract.
            </li>
          </ul>
        }
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <Input.Search
          placeholder="Filter keys (e.g. uam, commercial, publish)"
          allowClear
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: 300 }}
          aria-label="Filter permission keys"
        />
        <Segmented
          options={['All', ...resources]}
          value={lane}
          onChange={(v) => setLane(String(v))}
          size="small"
          style={{ maxWidth: '100%', overflowX: 'auto' }}
        />
      </div>

      <Table<MatrixKey>
        rowKey="key"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 260 + matrix.roles.length * 92, y: 520 }}
        locale={{ emptyText: 'No keys match the filter.' }}
      />
      <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 11.5 }}>
        ● = the role holds the key · — = it does not. Custom <span className="ol-mono">x_</span>{' '}
        roles are not columns here because they are per-hub compositions of these same keys — see the
        Custom roles tab for what each one binds.
      </Paragraph>
    </div>
  );
}
